// lib
import { CSSProperties, useState, useCallback, useEffect, useMemo } from 'react'

import {
  TableDataView,
  TableDataViewCellTitle,
  TableDataViewCol,
  TableDataViewRow
} from '@aytm/app/stats/components/data-views/table/types'

import TableHeader from '@aytm/app/stats/components/data-views/table/components/TableHeader'
import TableRow from '@aytm/app/stats/components/data-views/table/components/TableRow'

// styles
import style from '@aytm/app/stats/components/data-views/table/styles/Table.module.scss'

// the sorted column is the one carrying the `sorted` marker; descend into grouped sub-cols
const getColSorted = (items: TableDataViewCol[]): TableDataViewCol | undefined => {
  const item = items.find(v => 'sorted' in v)
  if (!item) {
    const _items = items.flatMap(item => ('cols' in item ? item.cols : []))
    if (0 < _items.length) return getColSorted(_items)
  }
  return item
}

// sort a COPY of the rows by the sorted column (never mutate the incoming rows prop); exported so
// an export can name the rows in the order the table shows them
export const sortRows = (rows: TableDataViewRow[], cols: TableDataViewCol[]) => {
  const colSorted = getColSorted(cols)
  if (colSorted?.sort) {
    return rows.slice().sort((a: TableDataViewRow, b: TableDataViewRow) => colSorted.sort(a, b, colSorted.sorted))
  }
  return rows
}

// The resting width of a figure slot, and what one more decimal place costs it. A share is drawn
// at one decimal by default; every further one the reader asks for is another digit to fit.
const FIGURE_WIDTH = 52
const DIGIT_WIDTH = 8

// The widest precision any column asks for: the slots line up down the whole table, so one column
// drawn at three decimals sizes them all.
const decimalsOf = (cols: TableDataViewCol[]): number =>
  cols.reduce(
    (max, col) =>
      Math.max(max, col.cols ? decimalsOf(col.cols) : Number.isFinite(col.percentDecimals) ? col.percentDecimals : 1),
    1
  )

const figureWidthOf = (cols: TableDataViewCol[]) => FIGURE_WIDTH + DIGIT_WIDTH * Math.max(0, decimalsOf(cols) - 1)

// A progress column heads two slots, a count and a percentage. Where its rows carry no count
// there is nothing for the left slot to head, so it is not drawn -- the column reads as the one
// figure it actually holds. Derived from the rows rather than declared, so a column that stops
// carrying counts (a report hiding them, a view holding only shares) needs nothing said about it.
const colsWithAmounts = (rows: TableDataViewRow[], cols: TableDataViewCol[]) =>
  cols.map(
    (col, index) =>
      'progress' != col.type ||
      rows.some(row => {
        const cell = row.cells?.[index]
        return Boolean(cell) && 'amount' in cell
      })
  )

// what a row's colour mark is drawn from; a row without one breaks the run around it
const swatchKeyOf = (row: TableDataViewRow, index: number) => {
  if (!row || ('type' in row && 'data' != row.type)) return null
  const cell = row.cells?.[index] as TableDataViewCellTitle
  if (!cell) return null
  if ('color' in cell) return cell.color
  if ('colorIndex' in cell) return `i${cell.colorIndex}`
  return null
}

// the column the mark is drawn beside; a list without an explicit title column carries it in the first
const titleIndexOf = (cols: TableDataViewCol[]) =>
  Math.max(
    0,
    cols.findIndex(col => 'title' == col.type)
  )

// Where each row sits in its run of same-coloured neighbours. A polarity list pairs its rows by
// giving both the same colour, and draws a thread between the pair's two marks.
const swatchRunsOf = (rows: TableDataViewRow[], cols: TableDataViewCol[]) => {
  const keys = rows.map(row => swatchKeyOf(row, titleIndexOf(cols)))

  return keys.map((key, index) => {
    if (key == undefined) return 'single'
    const prev = key === keys[index - 1]
    const next = key === keys[index + 1]
    return prev && next ? 'mid' : prev ? 'end' : next ? 'start' : 'single'
  })
}

// The mark's colour, handed to the row rather than only to the cell that declares it: the thread a
// run draws is the row's own, and it needs the colour to tint itself with. The palette publishes the
// colour as a custom property on its class, so a row carrying that class passes it down; a caller
// naming a colour outright is set as the same property directly.
const swatchPaintsOf = (rows: TableDataViewRow[], cols: TableDataViewCol[]) => {
  const titleIndex = titleIndexOf(cols)

  return rows.map(row => {
    if (!row || ('type' in row && 'data' != row.type)) return null
    const cell = row.cells?.[titleIndex] as TableDataViewCellTitle
    if (!cell) return null
    if ('color' in cell) return { color: cell.color }
    if ('colorIndex' in cell) return { className: `color-${cell.colorIndex}` }
    return null
  })
}

export default function Table(table: TableDataView) {
  const [cols, setCols] = useState(table.cols)
  const [rows, setRows] = useState(table.rows)

  const hasAmounts = useMemo(() => colsWithAmounts(rows, cols), [rows, cols])

  const onSort = useCallback(
    (colIndex: number, dir: 1 | -1) => {
      if ('onSort' in table && 'function' == typeof table.onSort) {
        return table.onSort(colIndex, dir, table.cols)
      } else {
        const removeSorted = (items: TableDataViewCol[]) =>
          items.map(item => {
            delete item.sorted
            if ('cols' in item) {
              item.cols = removeSorted(item.cols)
            }
            return item
          })
        const applySorted = (items: TableDataViewCol[], index: number) => {
          if (colIndex < index + items.length) {
            items[colIndex - index].sorted = dir
          } else {
            let prevColsLength = 0
            return items.map(item => {
              if ('cols' in item) {
                item.cols = applySorted(item.cols, items.length + prevColsLength)
                prevColsLength += item.cols.length
              } else prevColsLength += 1
              return item
            })
          }
          return items
        }

        // local sort path (no external onSort): apply the marker, then re-sort the rows by
        // THESE cols in the same tick — no reliance on the lagged `cols` state
        const newCols = applySorted(removeSorted(table.cols), 0)
        setCols(newCols)
        setRows(sortRows(table.rows, newCols))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [table.cols, table.rows, table.onSort]
  )

  const swatchRuns = useMemo(() => swatchRunsOf(rows, cols), [rows, cols])
  const swatchPaints = useMemo(() => swatchPaintsOf(rows, cols), [rows, cols])

  const figureWidth = useMemo(() => figureWidthOf(cols), [cols])

  // a new table carries rows and cols together — sync and sort atomically off table.cols,
  // so new rows are always sorted by their OWN cols (fixes sorting new rows by stale cols)
  useEffect(() => {
    setCols(table.cols)
    setRows(sortRows(table.rows, table.cols))
  }, [table.rows, table.cols])

  return (
    <div
      className={`${style.component}${
        table.classNames ? ` ${table.classNames.map(className => style[className]).join(' ')}` : ''
      }`}
      style={{ '--table-figure-width': `${figureWidth}px` } as CSSProperties}
    >
      <TableHeader id={table.id} index={0} cols={cols} hasAmounts={hasAmounts} onSort={onSort} />
      {rows.map((row: TableDataViewRow, index: number) => (
        <TableRow
          key={`table-row-${row.position}`}
          cols={cols}
          row={row}
          swatchRun={swatchRuns[index]}
          swatchPaint={swatchPaints[index]}
        />
      ))}
    </div>
  )
}
